import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, Input, Select } from "barte-design-system";
import { PixLogoIcon } from "@phosphor-icons/react";
import { usePostHog } from "@posthog/react";

import { InputNumber } from "@shared/components";
import { useValidationErrors } from "@payfac/hooks";
import { TRACK_EVENTS } from "@payfac/constants";

import { objectShape } from "./modal-edit-pix.constants";

import type { ModalEditPixProps } from "./modal-edit-pix.type";
import styles from "./modal-edit-pix.module.css";
import { formatPercentage } from "@shared/helpers";

export const ModalEditPix = ({
    isOpen,
    onClose,
    data,
    onHandleSubmit,
    isLoading,
    companyRate,
}: ModalEditPixProps) => {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [dataForm, setDataForm] = useState(data);
    const [initEdit, setInitEdit] = useState(false);

    const { isValid, errors, onHandleListenErrors } = useValidationErrors(
        objectShape(t),
        dataForm,
    );

    const onChangeValues = (
        key: keyof ModalEditPixProps["data"],
        value: ModalEditPixProps["data"][keyof ModalEditPixProps["data"]],
    ) => {
        setDataForm({ ...dataForm, [key]: value });
        setInitEdit(true);
    };

    const handleSave = () => {
        if (!isValid) {
            onHandleListenErrors(true);
            return;
        }

        onHandleSubmit(dataForm);
        posthog.capture(TRACK_EVENTS.RATE_PLAN.PIX_CLICKED);
    };

    const title = (
        <div className={styles["modal-header"]}>
            <PixLogoIcon /> {t("payfac.pageRatePlanModalEditPixTitle")}
        </div>
    ) as unknown as string;
    const hasDiff = JSON.stringify(dataForm) !== JSON.stringify(data);

    useEffect(() => {
        if (isOpen) {
            if (!initEdit) {
                setDataForm(data);
            }
        } else {
            onHandleListenErrors(false);
            setDataForm({} as ModalEditPixProps["data"]);
            setInitEdit(false);
        }
    }, [isOpen, data, initEdit]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="lg"
            footer={{
                primaryButton: {
                    children: t("payfac.pageRatePlanModalSaveButton"),
                    onClick: handleSave,
                    disabled: !hasDiff,
                    loading: isLoading,
                },
                secondaryButton: {
                    children: t("payfac.pageRatePlanModalCancelButton"),
                    variant: "outline",
                    onClick: onClose,
                    disabled: isLoading,
                },
                buttonLayout: "hug",
            }}
        >
            <div className={styles.content}>
                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatePlanModalRepassTimeLabel")}
                        value={dataForm?.delayForRedeem}
                        onChange={(value) => onChangeValues("delayForRedeem", value)}
                        prefix="D + "
                        decimalPrecision={0}
                        error={errors?.delayForRedeem}
                        helperText={errors?.delayForRedeem || ""}
                    />
                </div>

                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatePlanModalTotalChargedLabel")}
                        value={dataForm?.value}
                        onChange={(value) => onChangeValues("value", value)}
                        error={errors?.value}
                        helperText={errors?.value || ""}
                    />
                </div>

                <div className={styles.field}>
                    <Select
                        label={t("payfac.pageRatePlanModalRateTypeLabel")}
                        options={[
                            {
                                value: "PERCENTAGE",
                                label: t("payfac.pageRatePlanDetailsPercentage"),
                            },
                            { value: "FIXED", label: t("payfac.pageRatePlanDetailsFixed") },
                        ]}
                        value={data?.rateType || "PERCENTAGE"}
                        onChange={() => null}
                        disabled
                        hidePrefixIcon
                    />
                </div>

                <div className={styles.field}>
                    <InputNumber
                        label={t("payfac.pageRatePlanModalRateLimitLabel")}
                        value={dataForm?.valueLimit}
                        onChange={(value) => onChangeValues("valueLimit", value)}
                        prefix="R$ "
                        error={errors?.valueLimit}
                        helperText={errors?.valueLimit || ""}
                    />
                </div>
                <div className={styles.field}>
                    <Input
                        label={t("payfac.pageRatePlanModalBarteRateLabel")}
                        value={formatPercentage(companyRate)}
                        onChange={() => null}
                        disabled
                    />
                </div>
            </div>
        </Modal>
    );
};
